/**
 * ====================================================================
 * FIKO TEKNIK RESIM 2D - TIP TANIMLARI
 * ====================================================================
 *
 * CATIA/SolidWorks/AutoCAD seviyesinde profesyonel teknik resim sistemi
 * için tüm tip tanımları.
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';

// ====================================================================
// GENEL TIPLER
// ====================================================================

/** 2D Nokta */
export interface Point2D {
    x: number;
    y: number;
}

/** 2D Çizgi */
export interface Line2D {
    start: Point2D;
    end: Point2D;
}

/** 2D Bounding Box */
export interface BoundingBox2D {
    min: Point2D;
    max: Point2D;
}

/** 2D Dönüşüm Matrisi */
export interface Transform2D {
    position: Point2D;
    rotation: number; // derece
    scale: number;
}

// ====================================================================
// KAGIT VE SAYFA
// ====================================================================

/** Kağıt boyutları (ISO 216) */
export enum PaperSize {
    A0 = 'A0',
    A1 = 'A1',
    A2 = 'A2',
    A3 = 'A3',
    A4 = 'A4',
    CUSTOM = 'CUSTOM'
}

/** Kağıt boyut değerleri (mm) */
export const PAPER_DIMENSIONS: Record<PaperSize, { width: number; height: number }> = {
    [PaperSize.A0]: { width: 1189, height: 841 },
    [PaperSize.A1]: { width: 841, height: 594 },
    [PaperSize.A2]: { width: 594, height: 420 },
    [PaperSize.A3]: { width: 420, height: 297 },
    [PaperSize.A4]: { width: 297, height: 210 },
    [PaperSize.CUSTOM]: { width: 297, height: 210 }
};

/** Kağıt yönü */
export enum PaperOrientation {
    PORTRAIT = 'PORTRAIT',
    LANDSCAPE = 'LANDSCAPE'
}

/** Projeksiyon sistemi */
export enum ProjectionSystem {
    FIRST_ANGLE = 'FIRST_ANGLE',  // ISO/Avrupa
    THIRD_ANGLE = 'THIRD_ANGLE'   // ANSI/Amerika
}

/** Sayfa kenar boşlukları */
export interface SheetMargins {
    top: number;
    bottom: number;
    left: number;   // Cilt payı için genelde 20mm
    right: number;
}

/** Sayfa yapılandırması */
export interface SheetConfig {
    id?: string;
    name?: string;
    paperSize: PaperSize;
    orientation: PaperOrientation;
    customWidth?: number;
    customHeight?: number;
    margins: SheetMargins;
    projectionSystem: ProjectionSystem;
    showBorder: boolean;
    showGrid: boolean;
    gridSpacing: number;
    showTitleBlock: boolean;
}

/** Varsayılan sayfa yapılandırması */
export const DEFAULT_SHEET_CONFIG: SheetConfig = {
    paperSize: PaperSize.A3,
    orientation: PaperOrientation.LANDSCAPE,
    margins: { top: 10, bottom: 10, left: 20, right: 10 },
    projectionSystem: ProjectionSystem.FIRST_ANGLE,
    showBorder: true,
    showGrid: false,
    gridSpacing: 10,
    showTitleBlock: true
};

// ====================================================================
// GÖRÜNÜM TİPLERİ
// ====================================================================

/** Görünüm tipleri */
export enum ViewType {
    // Standart görünümler
    FRONT = 'FRONT',
    BACK = 'BACK',
    TOP = 'TOP',
    BOTTOM = 'BOTTOM',
    LEFT = 'LEFT',
    RIGHT = 'RIGHT',

    // Aksonometrik
    ISOMETRIC = 'ISOMETRIC',
    DIMETRIC = 'DIMETRIC',
    TRIMETRIC = 'TRIMETRIC',

    // Özel görünümler
    SECTION = 'SECTION',
    DETAIL = 'DETAIL',
    AUXILIARY = 'AUXILIARY',
    BROKEN = 'BROKEN',
    PARTIAL = 'PARTIAL'
}

/** Standart görünüm yönleri */
export const STANDARD_VIEW_DIRECTIONS: Record<string, THREE.Vector3> = {
    [ViewType.FRONT]: new THREE.Vector3(0, 0, 1),
    [ViewType.BACK]: new THREE.Vector3(0, 0, -1),
    [ViewType.TOP]: new THREE.Vector3(0, 1, 0),
    [ViewType.BOTTOM]: new THREE.Vector3(0, -1, 0),
    [ViewType.LEFT]: new THREE.Vector3(-1, 0, 0),
    [ViewType.RIGHT]: new THREE.Vector3(1, 0, 0),
    [ViewType.ISOMETRIC]: new THREE.Vector3(1, 1, 1).normalize()
};

/** Görünüm yapılandırması */
export interface ViewConfig {
    id?: string;
    name?: string;
    type: ViewType;
    position: Point2D;
    scale: number;
    rotation: number;
    showHiddenLines: boolean;
    showCenterLines: boolean;
    showCenterMarks: boolean;
    label?: string;

    // Özel görünümler için
    parentViewId?: string;
    viewDirection?: THREE.Vector3;
    upVector?: THREE.Vector3;
}

/** Varsayılan görünüm yapılandırması */
export const DEFAULT_VIEW_CONFIG: Partial<ViewConfig> = {
    scale: 1,
    rotation: 0,
    showHiddenLines: true,
    showCenterLines: true,
    showCenterMarks: true
};

// ====================================================================
// ÇİZGİ STİLLERİ (ISO 128)
// ====================================================================

/** Çizgi tipleri */
export enum LineType {
    CONTINUOUS_THICK = 'CONTINUOUS_THICK',   // Görünür kenarlar
    CONTINUOUS_THIN = 'CONTINUOUS_THIN',     // Boyut çizgileri
    DASHED = 'DASHED',                       // Gizli kenarlar
    CHAIN_THIN = 'CHAIN_THIN',               // Eksen çizgileri
    CHAIN_THICK = 'CHAIN_THICK',             // Kesit düzlemi
    PHANTOM = 'PHANTOM'                       // Hareket yolları
}

/** Çizgi kalınlıkları (mm) */
export const LINE_WEIGHTS: Record<LineType, number> = {
    [LineType.CONTINUOUS_THICK]: 0.5,
    [LineType.CONTINUOUS_THIN]: 0.25,
    [LineType.DASHED]: 0.35,
    [LineType.CHAIN_THIN]: 0.25,
    [LineType.CHAIN_THICK]: 0.5,
    [LineType.PHANTOM]: 0.25
};

/** Çizgi görünürlük durumu */
export enum LineVisibility {
    VISIBLE = 'VISIBLE',
    HIDDEN = 'HIDDEN',
    SILHOUETTE = 'SILHOUETTE'
}

/** Çizgi stili */
export interface LineStyle {
    type: LineType;
    weight: number;
    color: string;
    dashPattern?: number[];
}

// ====================================================================
// BOYUTLANDIRMA
// ====================================================================

/** Boyut tipleri */
export enum DimensionType {
    LINEAR_HORIZONTAL = 'LINEAR_HORIZONTAL',
    LINEAR_VERTICAL = 'LINEAR_VERTICAL',
    LINEAR_ALIGNED = 'LINEAR_ALIGNED',
    ANGULAR = 'ANGULAR',
    RADIUS = 'RADIUS',
    DIAMETER = 'DIAMETER',
    ARC_LENGTH = 'ARC_LENGTH',
    ORDINATE_X = 'ORDINATE_X',
    ORDINATE_Y = 'ORDINATE_Y',
    CHAIN = 'CHAIN',
    BASELINE = 'BASELINE',
    CHAMFER = 'CHAMFER'
}

/** Boyut stili */
export interface DimensionStyle {
    id: string;
    name: string;

    // Çizgi
    lineColor: string;
    lineWeight: number;
    extensionLineGap: number;
    extensionLineExtend: number;

    // Ok başları
    arrowType: ArrowType;
    arrowSize: number;

    // Metin
    textHeight: number;
    textColor: string;
    textFont: string;
    textGap: number;

    // Tolerans
    toleranceTextHeight: number;

    // Birim
    unit: 'mm' | 'inch';
    precision: number;
    showUnit: boolean;
}

/** Ok tipleri */
export enum ArrowType {
    CLOSED_FILLED = 'CLOSED_FILLED',
    CLOSED_BLANK = 'CLOSED_BLANK',
    OPEN = 'OPEN',
    DOT = 'DOT',
    TICK = 'TICK',
    NONE = 'NONE'
}

/** Varsayılan boyut stili (ISO) */
export const DEFAULT_DIMENSION_STYLE: DimensionStyle = {
    id: 'ISO-25',
    name: 'ISO-25',
    lineColor: '#000000',
    lineWeight: 0.25,
    extensionLineGap: 1,
    extensionLineExtend: 2,
    arrowType: ArrowType.CLOSED_FILLED,
    arrowSize: 2.5,
    textHeight: 2.5,
    textColor: '#000000',
    textFont: 'Arial',
    textGap: 1,
    toleranceTextHeight: 1.8,
    unit: 'mm',
    precision: 2,
    showUnit: false
};

/** Tolerans */
export interface Tolerance {
    upper?: number;
    lower?: number;
    symmetric?: boolean;
    fit?: string; // örn: "H7/g6"
}

/** Boyut verisi */
export interface DimensionData {
    id: string;
    type: DimensionType;
    referencePoints: Point2D[];
    dimensionLinePosition: Point2D;
    value: number;
    displayValue: string;
    tolerance?: Tolerance;
    prefix?: string;
    suffix?: string;
    textOverride?: string;
    style: DimensionStyle;
    layerId: string;
    viewId?: string;
}

// ====================================================================
// NOTLAR VE SEMBOLLER
// ====================================================================

/** Not tipleri */
export enum AnnotationType {
    TEXT_NOTE = 'TEXT_NOTE',
    LEADER_NOTE = 'LEADER_NOTE',
    BALLOON = 'BALLOON',
    GDT_FRAME = 'GDT_FRAME',
    SURFACE_FINISH = 'SURFACE_FINISH',
    WELD_SYMBOL = 'WELD_SYMBOL',
    DATUM_FEATURE = 'DATUM_FEATURE',
    CENTER_MARK = 'CENTER_MARK',
    CENTERLINE = 'CENTERLINE'
}

/** Not verisi */
export interface AnnotationData {
    id: string;
    type: AnnotationType;
    position: Point2D;
    attachmentPoint?: Point2D;
    leaderPoints?: Point2D[];
    content: any; // Tip bazlı içerik
    layerId: string;
    viewId?: string;
}

// ====================================================================
// KATMANLAR
// ====================================================================

/** Katman */
export interface Layer {
    id: string;
    name: string;
    visible: boolean;
    locked: boolean;
    printable: boolean;
    color: string;
    lineWeight: number;
    order: number;
}

/** Varsayılan katmanlar */
export const DEFAULT_LAYERS: Layer[] = [
    { id: 'views', name: 'Görünümler', visible: true, locked: false, printable: true, color: '#000000', lineWeight: 0.5, order: 0 },
    { id: 'dimensions', name: 'Boyutlar', visible: true, locked: false, printable: true, color: '#000000', lineWeight: 0.25, order: 1 },
    { id: 'annotations', name: 'Notlar', visible: true, locked: false, printable: true, color: '#000000', lineWeight: 0.25, order: 2 },
    { id: 'centerlines', name: 'Eksen Çizgileri', visible: true, locked: false, printable: true, color: '#0000FF', lineWeight: 0.18, order: 3 },
    { id: 'construction', name: 'Yardımcı', visible: false, locked: false, printable: false, color: '#808080', lineWeight: 0.18, order: 4 }
];

// ====================================================================
// KESİT GÖRÜNÜMÜ
// ====================================================================

/** Kesit tipi */
export enum SectionType {
    FULL = 'FULL',
    HALF = 'HALF',
    OFFSET = 'OFFSET',
    ALIGNED = 'ALIGNED',
    REMOVED = 'REMOVED',
    REVOLVED = 'REVOLVED',
    BROKEN_OUT = 'BROKEN_OUT'
}

/** Tarama deseni */
export enum HatchPattern {
    ANSI31 = 'ANSI31',      // Genel amaçlı
    ANSI32 = 'ANSI32',      // Çelik
    ANSI33 = 'ANSI33',      // Bronz/Pirinç
    ANSI34 = 'ANSI34',      // Plastik
    ANSI35 = 'ANSI35',      // Ateş tuğlası
    ANSI36 = 'ANSI36',      // Mermer
    ANSI37 = 'ANSI37',      // Dökme demir
    ANSI38 = 'ANSI38',      // Alüminyum
    SOLID = 'SOLID',
    NONE = 'NONE'
}

/** Kesit yapılandırması */
export interface SectionConfig {
    type: SectionType;
    label: string;          // örn: "A-A"
    planeOrigin: THREE.Vector3;
    planeNormal: THREE.Vector3;
    hatchPattern: HatchPattern;
    hatchAngle: number;
    hatchScale: number;
    showHatch: boolean;
}

// ====================================================================
// DETAY GÖRÜNÜMÜ
// ====================================================================

/** Detay görünümü yapılandırması */
export interface DetailConfig {
    label: string;          // örn: "B"
    centerPoint: Point2D;   // Kaynak görünümdeki merkez
    radius: number;         // Seçim dairesi yarıçapı
    scale: number;          // Büyütme faktörü
    showBorder: boolean;
    borderStyle: 'CIRCLE' | 'RECTANGLE';
}

// ====================================================================
// ANTET BLOĞU
// ====================================================================

/** Antet bloğu */
export interface TitleBlock {
    companyName: string;
    companyLogo?: string;
    projectName: string;
    partName: string;
    partNumber: string;
    material?: string;
    scale: string;
    date: string;
    drawnBy: string;
    checkedBy?: string;
    approvedBy?: string;
    revision: string;
    sheetNumber: number;
    totalSheets: number;
    customFields?: Record<string, string>;
}

// ====================================================================
// EXPORT
// ====================================================================

/** Export formatları */
export enum ExportFormat {
    PDF = 'PDF',
    DXF = 'DXF',
    DWG = 'DWG',
    SVG = 'SVG',
    PNG = 'PNG',
    JPEG = 'JPEG'
}

/** Export yapılandırması */
export interface ExportConfig {
    format: ExportFormat;
    resolution?: number;     // Raster için DPI
    includeAnnotations: boolean;
    includeDimensions: boolean;
    includeHiddenLines: boolean;
    colorMode: 'COLOR' | 'GRAYSCALE' | 'MONOCHROME';
    lineWeightScale: number;
}

// ====================================================================
// PROJEKSIYON SONUCU
// ====================================================================

/** Projeksiyon sonucu */
export interface ProjectionResult {
    visibleLines: Line2D[];
    hiddenLines: Line2D[];
    silhouetteLines: Line2D[];
    centerLines: Line2D[];
    boundingBox: BoundingBox2D;
    processingTimeMs: number;
}

// ====================================================================
// ELEMENT DURUMU
// ====================================================================

/** Element seçim durumu */
export interface SelectionState {
    selected: boolean;
    hovered: boolean;
    highlighted: boolean;
}

/** Element genel durumu */
export interface ElementState {
    visible: boolean;
    locked: boolean;
    selection: SelectionState;
}

// ====================================================================
// ARAÇ SİSTEMİ
// ====================================================================

/** Araç tipleri */
export enum ToolType {
    SELECT = 'SELECT',
    PAN = 'PAN',
    ZOOM = 'ZOOM',

    // Görünüm araçları
    VIEW_FRONT = 'VIEW_FRONT',
    VIEW_TOP = 'VIEW_TOP',
    VIEW_RIGHT = 'VIEW_RIGHT',
    VIEW_LEFT = 'VIEW_LEFT',
    VIEW_BACK = 'VIEW_BACK',
    VIEW_BOTTOM = 'VIEW_BOTTOM',
    VIEW_ISOMETRIC = 'VIEW_ISOMETRIC',
    VIEW_SECTION = 'VIEW_SECTION',
    VIEW_DETAIL = 'VIEW_DETAIL',
    VIEW_AUXILIARY = 'VIEW_AUXILIARY',

    // Boyut araçları
    DIM_LINEAR = 'DIM_LINEAR',
    DIM_ANGULAR = 'DIM_ANGULAR',
    DIM_RADIUS = 'DIM_RADIUS',
    DIM_DIAMETER = 'DIM_DIAMETER',
    DIM_ORDINATE = 'DIM_ORDINATE',
    DIM_CENTER_MARK = 'DIM_CENTER_MARK',
    DIM_CENTERLINE = 'DIM_CENTERLINE',

    // Not araçları
    NOTE_TEXT = 'NOTE_TEXT',
    NOTE_BALLOON = 'NOTE_BALLOON',
    NOTE_GDT = 'NOTE_GDT',
    NOTE_SURFACE = 'NOTE_SURFACE',
    NOTE_WELD = 'NOTE_WELD',
    NOTE_DATUM = 'NOTE_DATUM'
}

/** Araç durumu */
export interface ToolState {
    type: ToolType;
    active: boolean;
    cursor: string;
    options?: Record<string, any>;
}

// ====================================================================
// SNAP SİSTEMİ
// ====================================================================

/** Snap tipleri */
export enum SnapType {
    ENDPOINT = 'ENDPOINT',
    MIDPOINT = 'MIDPOINT',
    CENTER = 'CENTER',
    INTERSECTION = 'INTERSECTION',
    PERPENDICULAR = 'PERPENDICULAR',
    TANGENT = 'TANGENT',
    NEAREST = 'NEAREST',
    GRID = 'GRID'
}

/** Snap noktası */
export interface SnapPoint {
    type: SnapType;
    position: Point2D;
    sourceElement?: string;
}

// ====================================================================
// COMMAND SİSTEMİ (UNDO/REDO)
// ====================================================================

/** Command arayüzü */
export interface IDrawingCommand {
    id: string;
    name: string;
    execute(): void;
    undo(): void;
    redo(): void;
    canMerge?(other: IDrawingCommand): boolean;
    merge?(other: IDrawingCommand): IDrawingCommand;
}

// ====================================================================
// EVENT SİSTEMİ
// ====================================================================

/** Event tipleri */
export enum DrawingEventType {
    // Document events
    DOCUMENT_CREATED = 'DOCUMENT_CREATED',
    DOCUMENT_MODIFIED = 'DOCUMENT_MODIFIED',
    DOCUMENT_SAVED = 'DOCUMENT_SAVED',

    // Sheet events
    SHEET_ADDED = 'SHEET_ADDED',
    SHEET_REMOVED = 'SHEET_REMOVED',
    SHEET_ACTIVATED = 'SHEET_ACTIVATED',

    // View events
    VIEW_ADDED = 'VIEW_ADDED',
    VIEW_REMOVED = 'VIEW_REMOVED',
    VIEW_MODIFIED = 'VIEW_MODIFIED',
    VIEW_MOVED = 'VIEW_MOVED',

    // Selection events
    SELECTION_CHANGED = 'SELECTION_CHANGED',

    // Tool events
    TOOL_ACTIVATED = 'TOOL_ACTIVATED',
    TOOL_DEACTIVATED = 'TOOL_DEACTIVATED',

    // Export events
    EXPORT_STARTED = 'EXPORT_STARTED',
    EXPORT_COMPLETED = 'EXPORT_COMPLETED',
    EXPORT_FAILED = 'EXPORT_FAILED'
}

/** Event verisi */
export interface DrawingEvent {
    type: DrawingEventType;
    timestamp: number;
    data?: any;
}
